import React, { useCallback, useEffect, useMemo, useState } from "react";

import cn from "classnames";

import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";

import isClosedStage from "../../util/isClosedStage";

import StageList from "./StageList";
import styles from "./StagesCarousel.module.scss";

interface Props {
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  onChange?: (stage: Stage) => void;
  stages: Stage[];
  value: Stage;
}

const StagesCarousel: React.FC<Props> = ({
  className,
  disabled,
  loading,
  onChange,
  stages,
  value,
}) => {
  const openStages = useMemo(() => stages.filter((stage) => !isClosedStage(stage)), [stages]);

  const [selectedStageIndex, setSelectedStageIndex] = useState<number>(0);
  useEffect(() => {
    setSelectedStageIndex(openStages.findIndex(({ id }) => id === value.id));
  }, [openStages, setSelectedStageIndex, value]);

  const notifyAboutChanges = useDebouncedCallback(
    [
      (index: number) => {
        if (onChange && index >= 0 && value.id !== openStages[index]?.id) {
          onChange(openStages[index]);
        }
      },
      1000,
    ],
    [onChange, openStages, value]
  );
  useEffect(() => notifyAboutChanges(selectedStageIndex), [notifyAboutChanges, selectedStageIndex]);

  const handlePreviousClick = useCallback(
    () => setSelectedStageIndex((index) => (index > 1 ? index - 1 : 0)),
    [setSelectedStageIndex]
  );
  const handleNextClick = useCallback(
    () =>
      setSelectedStageIndex((index) =>
        index < openStages.length - 2 ? index + 1 : openStages.length - 1
      ),
    [openStages, setSelectedStageIndex]
  );

  if (isClosedStage(value)) {
    return null;
  }

  return (
    <div className={cn(styles.container, className)}>
      <StageList
        disabled={disabled}
        loading={loading}
        onNextClick={handleNextClick}
        onPreviousClick={handlePreviousClick}
        selectedIndex={selectedStageIndex}
        stages={openStages}
      />
    </div>
  );
};

export default StagesCarousel;
